import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from ".";
import { Button } from "../ui/button";
import {
  CheckCircle2,
  XCircle,
  Eye,
  User,
  Mail,
  IdCard,
  Award,
  Building2,
  Calendar,
  BookOpen,
  Send,
  BadgeCheck,
  Loader2,
} from "lucide-react";
import {
  useResendOnboardingEmail,
  useResendSelfOnboardingEmail,
} from "../../hooks/useEnrollment";

export function StudentsTable({ data }) {
  const [globalFilter, setGlobalFilter] = useState("");
  const navigate = useNavigate();

  const { mutate: resendPartnerEmail, isPending: isPartnerPending } =
    useResendOnboardingEmail();
  const { mutate: resendSelfEmail, isPending: isSelfPending } =
    useResendSelfOnboardingEmail();

  const [loadingId, setLoadingId] = useState(null);

  const handleResendClick = useCallback((student) => {
    setLoadingId(student._id);

    const options = {
      onSettled: () => setLoadingId(null),
    };

    if (student.partnerName) {
      resendPartnerEmail(student._id, options);
    } else {
      resendSelfEmail(student._id, options);
    }
  }, [resendPartnerEmail, resendSelfEmail]);

  const rows = Array.isArray(data) ? data : [];

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: () => (
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-500" />
            <span>Name</span>
          </div>
        ),
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        cell: (info) => (
          <div className="font-medium text-gray-900">{info.getValue()}</div>
        ),
      },
      {
        id: "email",
        accessorKey: "email",
        header: () => (
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-500" />
            <span>Email</span>
          </div>
        ),
        cell: (info) => (
          <div className="text-gray-600 text-sm">{info.getValue()}</div>
        ),
      },
      {
        id: "registrationNumber",
        header: () => (
          <div className="flex items-center gap-2">
            <IdCard className="w-4 h-4 text-gray-500" />
            <span>Registration No.</span>
          </div>
        ),
        accessorKey: "registrationNumber",
        cell: (info) => {
          const regNumber = info.row.original?.registrationNumber;
          return (
            <div className="font-mono text-sm text-gray-700">
              {regNumber || (
                <span className="text-gray-400 italic">Not assigned</span>
              )}
            </div>
          );
        },
      },
      {
        id: "membershipId",
        header: () => (
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-gray-500" />
            <span>Membership Type</span>
          </div>
        ),
        accessorKey: "membershipId",
        cell: (info) => {
          const membership = info.row.original?.membershipId?.title;
          return membership ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {membership}
            </span>
          ) : (
            <span className="text-gray-400 italic text-sm">None</span>
          );
        },
      },
      {
        id: "partnerName",
        header: () => (
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-500" />
            <span>Account Type</span>
          </div>
        ),
        accessorKey: "partnerName",
        cell: (info) => {
          const partner = info.row.original?.partnerName;
          return partner ? (
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded w-fit mb-1 border border-purple-100">
                Partner Student
              </span>
              <span className="text-gray-700 text-sm font-medium">
                {partner}
              </span>
            </div>
          ) : (
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded w-fit mb-1 border border-gray-200">
                Individual
              </span>
              <span className="text-gray-400 text-sm">Self-Registered</span>
            </div>
          );
        },
      },
      {
        id: "emailVerified",
        header: () => (
          <div className="flex items-center gap-2">
            <BadgeCheck className="w-4 h-4 text-gray-500" />
            <span>Status</span>
          </div>
        ),
        accessorKey: "emailVerified",
        cell: (info) => {
          const isVerified = info.row.original?.isEmailVerified;
          const student = info.row.original;
          const isRowLoading = loadingId === student._id;
          const isGlobalPending = isPartnerPending || isSelfPending;

          return (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 w-28">
                {isVerified ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-green-700 font-medium text-sm">
                      Verified
                    </span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="text-red-600 font-medium text-sm">
                      Pending
                    </span>
                  </>
                )}
              </div>

              {!isVerified && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isGlobalPending}
                  onClick={() => handleResendClick(student)}
                  className="flex items-center gap-1 h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200 hover:border-blue-300 shadow-sm disabled:opacity-50"
                  title="Resend verification email"
                >
                  {isRowLoading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  <span className="text-xs font-medium">
                    {isRowLoading ? "Sending..." : "Resend Invite"}
                  </span>
                </Button>
              )}
            </div>
          );
        },
      },
      {
        id: "createdAt",
        header: () => (
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <span>Date Joined</span>
          </div>
        ),
        accessorKey: "createdAt",
        cell: (info) => {
          const date = new Date(info.row.original.createdAt);
          return (
            <div className="text-gray-600 text-sm">
              {date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
          );
        },
      },
      {
        id: "courseEnrollments",
        header: () => (
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-gray-500" />
            <span>Courses</span>
          </div>
        ),
        cell: (info) => {
          const student = info.row.original;
          const count = Array.isArray(student?.courseEnrollments)
            ? student.courseEnrollments.length
            : 0;
          return (
            <div className="flex items-center">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-50 border border-gray-200 text-gray-700 font-semibold text-xs">
                {count}
              </span>
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const student = info.row.original;
          return (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/users/student/${student._id}`)}
              className="flex items-center gap-1.5 hover:bg-gray-50"
            >
              <Eye className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">View</span>
            </Button>
          );
        },
      },
    ],
    [navigate, loadingId, isPartnerPending, isSelfPending, handleResendClick],
  );

  return (
    <div className="bg-white p-2 rounded-lg shadow-sm border border-gray-200">
      <DataTable
        data={rows}
        columns={columns}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        pageSize={5}
      />
    </div>
  );
}
